import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import Page from '../../../Components/Page';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateOrganizationMutation,
  useCreateOrganizationPropertiesMutation,
  useCreateOrganizationAndRelationshipMutation,
  useDeleteOrganizationMutation,
  useRequestOrganizationDetailsQuery,
  useRequestOrganizationsTypeQuery,
  useUpdateOrganizationMutation,
  useUpdateOrganizationPropertiesMutation,
  useCreateParentOrganizationLinkMutation,
  useDeleteParentOrganizationLinkMutation
} from '../../../redux/api/organizations.js';
import LoadingAnimation from '../../../Components/Utils/BateryChargeAnimation/index.jsx';
import IfRender from '../../../Components/Utils/IfRender/index.jsx';
import Button from '../../../Components/Utils/Button/index.jsx';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import OrganizationForm from '../../../Components/Forms/Organization/index.jsx';
import ProviderSelectForm from '../../../Components/Forms/ProviderSelect/index.jsx';
import OrganizationChargePointsList from '../../../Components/Forms/OrganizationChargePointsList/index.jsx';
import OrganizationUserList from '../../../Components/Forms/OrganizationUserList/index.jsx';
import OrganizationAdditionalProperties from '../../../Components/Forms/OrganizationAdditionalProperties/index.jsx';
import { organizationDetailFilter } from '../../../QueryFilters/queries.js';
import { ORGANIZATION_TYPE } from '../../../Constants.js';

import './style.scss';

function OrganizationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const filter = JSON.stringify(organizationDetailFilter);
  const [organizationType, setOrganizationType] = useState(0);

  const {
    data,
    refetch,
    isFetching: isOrganizationFetching
  } = useRequestOrganizationDetailsQuery(
    { id, filter },
    { skip: !id } // Skip the query when id is undefined
  );

  const [isProviderSelected, setIsProviderSelected] = useState(false);
  const [
    updateOrganization,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess
    }
  ] = useUpdateOrganizationMutation();

  const [updateOrganizationProperties, {}] = useUpdateOrganizationPropertiesMutation();
  const [createOrganizationProperties, {}] = useCreateOrganizationPropertiesMutation();
  const [
    createOrganizationAndRelationship,
    { error: createOrgRelationshipError, isError: isCreateOrgRelationshipError }
  ] = useCreateOrganizationAndRelationshipMutation();
  const [createOrganizationParentRelation, {}] = useCreateParentOrganizationLinkMutation();
  const [deleteOrganizationParentRelation, {}] = useDeleteParentOrganizationLinkMutation();
  const { data: organizationTypes, isSuccess: isOrgTypesSuccess } =
    useRequestOrganizationsTypeQuery();

  const organizationTypeId = organizationTypes?.find(
    (type) => type.name === ORGANIZATION_TYPE.BASE
  )?.id;
  const [
    createOrganization,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      isSuccess: isCreateSuccess,
      error: createError
    }
  ] = useCreateOrganizationMutation();

  const [
    deleteOrganization,
    {
      isLoading: isDeleteLoading,
      isError: isDeleteError,
      error: deleteError,
      isSuccess: isDeleteSuccess
    }
  ] = useDeleteOrganizationMutation();

  useEffect(() => {
    if (isUpdateSuccess || isCreateSuccess || isDeleteSuccess) {
      navigate(`/organizations`);
    }
  }, [isUpdateSuccess, isCreateSuccess, isDeleteSuccess]);

  useEffect(() => {
    if (id) {
      void refetch({ id });
    }
  }, [id]);

  let organizationPropertiesObject;
  if (data?.organizationProperties) {
    organizationPropertiesObject = data?.organizationProperties.reduce((result, property) => {
      result[property.propertyName] = property.propertyValue;
      return result;
    }, {});
  }
  const initialValues = useMemo(() => {
    return {
      organization_name: data?.name,
      provider_id: '',
      ...organizationPropertiesObject
    };
  }, [data]);

  const form = useForm({ values: initialValues });
  const {
    handleSubmit,
    formState: { errors }
  } = form;

  useEffect(() => {
    if (isCreateError || isUpdateError || createOrgRelationshipError) {
      form.setError('generic', {
        message:
          updateError?.data?.message ||
          createError?.data?.message ||
          createOrgRelationshipError?.data?.error?.message ||
          'error_generic_request'
      });
    }
  }, [isCreateError, isUpdateError, isCreateOrgRelationshipError]);

  const createOrganizationConfigs = async (values, response) => {
    const keysToExclude = ['provider_id', 'organization_name'];
    for (let key in values) {
      if (!keysToExclude.includes(key) && values[key] !== '') {
        await createOrganizationProperties({
          data: { propertyValue: values[key], propertyName: key },
          id: response.data.id
        });
      }
    }
  };
  const updateOrganizationConfigs = async (values) => {
    const keysToExclude = ['provider_id', 'organization_name'];
    for (let key in values) {
      if (!keysToExclude.includes(key) || key !== null) {
        await updateOrganizationProperties({
          data: { propertyValue: values[key], propertyName: key },
          id,
          where: { propertyName: key }
        });
      }
    }
  };
  const onSubmit = async (values) => {
    if (id) {
      await updateOrganization({
        param: {
          name: values.organization_name
        },
        id: id
      }).then(() => {
        void updateOrganizationConfigs(values);
        if (!!data?.organizationParentOrganization[0]?.parentOrganizationId) {
          deleteOrganizationParentRelation({
            where: {
              parentOrganizationId: data?.organizationParentOrganization[0]?.parentOrganizationId
            },
            id: id
          }).then(() => {
            void createOrganizationParentRelation({
              id: id,
              body: { parentOrganizationId: parseInt(values.provider_id) }
            });
          });
        }
      });
    } else {
      await createOrganizationAndRelationship({
        data: {
          name: values.organization_name,
          organizationTypeId: organizationTypeId
        },
        id: values.provider_id
      }).then(async (response) => {
        if (response) {
          await createOrganizationConfigs(values, response);
        }
      });
    }
  };
  const parentOrganizationId =
    data?.organizationParentOrganization?.[0]?.parentOrganizationId ?? null;

  return (
    <Page title={`${t('organization')} ${data?.organization_name || ''}`} customHeaderItems={[]}>
      <IfRender condition={(id && !data && isOrganizationFetching) || isUpdateLoading}>
        <LoadingAnimation />
      </IfRender>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className='form'>
          <OrganizationForm readOnly={isUpdateLoading} />
          <OrganizationAdditionalProperties readOnly={isUpdateLoading} />
          {!isProviderSelected && isOrgTypesSuccess && (
            <ProviderSelectForm
              currentOrganizationId={parentOrganizationId}
              readOnly={isUpdateLoading}
              providerOrg={organizationTypes}
            />
          )}

          {id && <OrganizationUserList organizationId={id} />}
          {id && <OrganizationChargePointsList organizationId={id} />}

          <div className='form__section-footer_buttons'>
            <Button
              disabled={false}
              className='button button__alfa'
              label={t('save')}
              type={'submit'}
            />
            <IfRender condition={errors?.generic}>
              <label className={`organization_error`}>{t(errors?.generic?.message)}</label>
            </IfRender>
            <IfRender condition={id}>
              <Button
                disabled={false}
                className='button button__bravo'
                label={t('delete')}
                type={'button'}
                onClick={() => {
                  confirm(t('organization_deletion_first_confirmation')) &&
                    confirm(t('organization_deletion_second_confirmation')) &&
                    confirm(t('organization_deletion_third_confirmation')) &&
                    deleteOrganization({ id });
                }}
              />
            </IfRender>
          </div>
        </form>
      </FormProvider>
    </Page>
  );
}

export default OrganizationDetails;

OrganizationDetails.propTypes = {
  id: PropTypes.string
};
